import React, { useState } from 'react';
import MainLayout from '../layout/MainLayout';
import CohortDefinition from '../components/dataRequest/CohortDefinition';
import DataVariables from '../components/dataRequest/DataVariables';
import AnalysisPlan from '../components/dataRequest/AnalysisPlan';
import RequestSummary from '../components/dataRequest/RequestSummary';

// Verwenden von Standard-HTML-Elementen statt MUI-Komponenten
// um Abhängigkeitsprobleme zu umgehen
const steps = [
  'Kohortendefinition',
  'Datenvariablen',
  'Analyseplan',
  'Zusammenfassung'
];

// Einfache Stepper-Komponente ohne MUI
const SimpleStepper = ({ steps, activeStep }) => {
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between',
      margin: '20px 0',
      padding: '20px',
      backgroundColor: '#fff',
      borderRadius: '8px',
      border: '1px solid #e0e0e0'
    }}>
      {steps.map((label, index) => (
        <div key={label} style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flex: 1
        }}>
          <div style={{
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            backgroundColor: index <= activeStep ? '#FF7A00' : '#e0e0e0',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '8px',
            fontWeight: 'bold'
          }}>
            {index + 1}
          </div>
          <div style={{
            color: index <= activeStep ? '#FF7A00' : '#718096',
            fontWeight: index === activeStep ? 'bold' : 'normal'
          }}>
            {label}
          </div>
          {index < steps.length - 1 && (
            <div style={{
              height: '1px',
              width: '100%',
              backgroundColor: index < activeStep ? '#FF7A00' : '#e0e0e0',
              position: 'absolute',
              left: `calc(${(index + 0.5) * (100 / steps.length)}% + 15px)`,
              right: `calc(${(index + 1.5) * (100 / steps.length)}% - 15px)`,
              top: '30px'
            }} />
          )}
        </div>
      ))}
    </div>
  );
};

// Einfache Alert-Komponente ohne MUI
const SimpleAlert = ({ severity, children }) => {
  const colors = {
    error: { bg: '#FEE2E2', border: '#F87171', text: '#B91C1C' },
    success: { bg: '#D1FAE5', border: '#34D399', text: '#065F46' },
    warning: { bg: '#FEF3C7', border: '#FBBF24', text: '#92400E' },
    info: { bg: '#DBEAFE', border: '#60A5FA', text: '#1E40AF' }
  };
  
  const style = colors[severity] || colors.info;
  
  return (
    <div style={{
      padding: '12px 16px',
      backgroundColor: style.bg,
      borderLeft: `4px solid ${style.border}`,
      color: style.text,
      borderRadius: '4px',
      marginBottom: '16px'
    }}>
      {children}
    </div>
  );
};

const DataRequestPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [savedCohorts, setSavedCohorts] = useState([]);
  const [savedVariableSets, setSavedVariableSets] = useState([]);
  const [savedAnalysisPlans, setSavedAnalysisPlans] = useState([]);
  const [savedRequests, setSavedRequests] = useState([]);
  
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [selectedVariables, setSelectedVariables] = useState(null);
  const [selectedAnalysisPlan, setSelectedAnalysisPlan] = useState(null);
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleNext = () => {
    if (activeStep === 0 && !selectedCohort) {
      setError('Bitte definieren und speichern Sie eine Kohorte, bevor Sie fortfahren.');
      return;
    }
    
    if (activeStep === 1 && !selectedVariables) {
      setError('Bitte wählen und speichern Sie Datenvariablen, bevor Sie fortfahren.');
      return;
    }
    
    if (activeStep === 2 && !selectedAnalysisPlan) {
      setError('Bitte wählen und speichern Sie einen Analyseplan, bevor Sie fortfahren.');
      return;
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setError('');
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError('');
  };

  const handleSaveCohort = (cohort) => {
    // Prüfen, ob eine Kohorte mit diesem Namen bereits existiert
    const existingIndex = savedCohorts.findIndex(c => c.name === cohort.name);
    
    if (existingIndex !== -1) {
      // Aktualisiere die bestehende Kohorte
      const updatedCohorts = [...savedCohorts];
      updatedCohorts[existingIndex] = cohort;
      setSavedCohorts(updatedCohorts);
    } else {
      // Füge eine neue Kohorte hinzu
      setSavedCohorts([...savedCohorts, cohort]);
    }
    
    setSelectedCohort(cohort);
    setSuccess('Kohorte erfolgreich gespeichert.');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSaveVariables = (variables) => {
    // Prüfen, ob ein Variablenset mit diesem Namen bereits existiert
    const existingIndex = savedVariableSets.findIndex(v => v.name === variables.name);
    
    if (existingIndex !== -1) {
      // Aktualisiere das bestehende Variablenset
      const updatedVariableSets = [...savedVariableSets];
      updatedVariableSets[existingIndex] = variables;
      setSavedVariableSets(updatedVariableSets);
    } else {
      // Füge ein neues Variablenset hinzu
      setSavedVariableSets([...savedVariableSets, variables]);
    }
    
    setSelectedVariables(variables);
    setSuccess('Variablenset erfolgreich gespeichert.');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSaveAnalysisPlan = (plan) => {
    // Prüfen, ob ein Analyseplan mit diesem Namen bereits existiert
    const existingIndex = savedAnalysisPlans.findIndex(p => p.name === plan.name);
    
    if (existingIndex !== -1) {
      // Aktualisiere den bestehenden Analyseplan
      const updatedAnalysisPlans = [...savedAnalysisPlans];
      updatedAnalysisPlans[existingIndex] = plan;
      setSavedAnalysisPlans(updatedAnalysisPlans);
    } else {
      // Füge einen neuen Analyseplan hinzu
      setSavedAnalysisPlans([...savedAnalysisPlans, plan]);
    }
    
    setSelectedAnalysisPlan(plan);
    setSuccess('Analyseplan erfolgreich gespeichert.');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSaveRequest = (request) => {
    // Prüfen, ob eine Anfrage mit diesem Namen bereits existiert
    const existingIndex = savedRequests.findIndex(r => r.name === request.name);
    
    if (existingIndex !== -1) {
      // Aktualisiere die bestehende Anfrage
      const updatedRequests = [...savedRequests];
      updatedRequests[existingIndex] = request;
      setSavedRequests(updatedRequests);
    } else {
      // Füge eine neue Anfrage hinzu
      setSavedRequests([...savedRequests, request]);
    }
    
    setSuccess('Anfrage erfolgreich als Entwurf gespeichert.');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSubmitRequest = (request) => {
    // Hier würde in einer realen Anwendung die Anfrage an den Server gesendet werden
    // Für dieses Beispiel speichern wir sie einfach lokal
    handleSaveRequest({...request, status: 'submitted'});
    setSuccess('Anfrage erfolgreich eingereicht. Sie erhalten eine Bestätigung per E-Mail.');
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <CohortDefinition onSaveCohort={handleSaveCohort} savedCohorts={savedCohorts} />;
      case 1:
        return <DataVariables onSaveVariables={handleSaveVariables} savedVariableSets={savedVariableSets} />;
      case 2:
        return (
          <AnalysisPlan 
            onSaveAnalysisPlan={handleSaveAnalysisPlan} 
            cohort={selectedCohort} 
            variables={selectedVariables} 
          />
        );
      case 3:
        return (
          <RequestSummary 
            cohort={selectedCohort} 
            variables={selectedVariables} 
            analysisPlan={selectedAnalysisPlan} 
            onSaveRequest={handleSaveRequest} 
            onSubmitRequest={handleSubmitRequest}
            activeStep={activeStep}
            steps={steps}
          />
        );
      default:
        return 'Unbekannter Schritt';
    }
  };

  return (
    <MainLayout>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ 
            fontSize: '2rem', 
            fontWeight: 700, 
            color: '#2D3748',
            marginBottom: '8px'
          }}>
            Neue Datenanfrage erstellen
          </h1>
          <p style={{ 
            fontSize: '1rem', 
            color: '#718096',
            marginBottom: '16px'
          }}>
            Definieren Sie Ihre Kohorte, wählen Sie Datenvariablen und einen Analyseplan aus, um eine neue Datenanfrage zu erstellen.
          </p>
          
          <SimpleStepper activeStep={activeStep} steps={steps} />
          
          {error && (
            <SimpleAlert severity="error">
              {error}
            </SimpleAlert>
          )}
          
          {success && (
            <SimpleAlert severity="success">
              {success}
            </SimpleAlert>
          )}
          
          {getStepContent(activeStep)}
          
          <hr style={{ 
            margin: '32px 0',
            border: 'none',
            borderTop: '1px solid #E2E8F0'
          }} />
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between'
          }}>
            <button
              style={{
                padding: '8px 16px',
                backgroundColor: 'transparent',
                color: activeStep === 0 ? '#A0AEC0' : '#FF7A00',
                border: `1px solid ${activeStep === 0 ? '#E2E8F0' : '#FF7A00'}`,
                borderRadius: '4px',
                cursor: activeStep === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '0.875rem',
                fontWeight: 600,
                opacity: activeStep === 0 ? 0.5 : 1
              }}
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              ← Zurück
            </button>
            
            {activeStep === steps.length - 1 ? (
              <button
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#FF7A00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  opacity: 0.5
                }}
                disabled={true}
              >
                Fertigstellen
              </button>
            ) : (
              <button
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#FF7A00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}
                onClick={handleNext}
              >
                Weiter →
              </button>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default DataRequestPage;

// Hinweis: Diese Implementierung verwendet einfache HTML/CSS statt MUI-Komponenten,
// um Probleme mit fehlenden Abhängigkeiten zu umgehen. In einer Produktionsumgebung
// würden wir die MUI-Komponenten verwenden, nachdem alle Abhängigkeiten korrekt installiert sind.
